import { DateString, DayOfWeek, getDayOfWeek } from './date_string.ts';
import { Household, HouseholdStatus, Org } from './model.ts';
import { Pages } from './pages.ts';
import { deriveTrialState } from './trial_state.ts';

export type SubscriptionBannerDecision =
  | { variant: 'hidden' }
  | { variant: 'subscribe' }
  | { variant: 'opt-in' }
  | { variant: 'invite' }
  | { variant: 'active-trial'; daysLeft: number }
  | { variant: 'expired-trial' };

export type SongClickGateDecision = { variant: 'allow' } | { variant: 'gate'; trialStatus: 'pre-trial' | 'expired' };

// A church that pays for its households under either billing model: the Legacy household add-on
// (areHouseholdsSubscribed, members opt in) or Usage-Based (isUsageBased, billed per active household).
// Mirrors the notInterested guard used throughout this module.
export function isSponsoringOrg(
  organization?: Pick<Org, 'areHouseholdsSubscribed' | 'isUsageBased' | 'notInterested'>,
): boolean {
  if (!organization || organization.notInterested) {
    return false;
  }
  return !!organization.areHouseholdsSubscribed || !!organization.isUsageBased;
}

export function decideSubscriptionBanner({
  page,
  organization,
  household,
  clientDate,
  isBypassUser = false,
  isOptimisticallySubscribed = false,
}: {
  page?: Pages;
  organization?: Pick<Org, 'areHouseholdsSubscribed' | 'isHouseholdChoice' | 'isUsageBased' | 'notInterested'>;
  household?: Pick<Household, 'status' | 'trialStartDate' | 'isDiscounted'>;
  clientDate: DateString;
  isBypassUser?: boolean;
  isOptimisticallySubscribed?: boolean;
}): SubscriptionBannerDecision {
  const hasOrganization = !!organization && !organization.notInterested;
  // discounted households (GitHub issue #3399) have full-library access without a subscription, and
  // Usage-Based Organizations pay for active households automatically - neither needs a subscribe prompt or opt-in
  const isSubscribed =
    household?.status === HouseholdStatus.Subscribed ||
    !!household?.isDiscounted ||
    isOptimisticallySubscribed ||
    (hasOrganization && !!organization.isUsageBased);

  switch (page) {
    case Pages.Library: {
      if (isSubscribed || isBypassUser) {
        return { variant: 'hidden' };
      }
      if (hasOrganization && organization.areHouseholdsSubscribed) {
        return { variant: 'opt-in' };
      }
      return getTrialAwareSubscriptionBannerDecision({ household, clientDate });
    }

    case Pages.Favorites: {
      if (hasOrganization && !organization.isHouseholdChoice) {
        return { variant: 'hidden' };
      }
      if (isSubscribed || isBypassUser) {
        return { variant: 'hidden' };
      }
      return getTrialAwareSubscriptionBannerDecision({ household, clientDate });
    }

    case Pages.Today:
    case Pages.Organization: {
      if (!hasOrganization) {
        return { variant: 'invite' };
      }
      if (
        !organization.isHouseholdChoice ||
        isSubscribed ||
        isBypassUser ||
        hasHouseholdChoiceSundaySongAccess({ page, organization, clientDate })
      ) {
        return { variant: 'hidden' };
      }
      return getTrialAwareSubscriptionBannerDecision({ household, clientDate });
    }

    default:
      return { variant: 'hidden' };
  }
}

export function decideSongClickGate({
  page,
  organization,
  household,
  clientDate,
  isOrganizationHymnal = false,
  isBypassUser = false,
  isOptimisticallySubscribed = false,
}: {
  page?: Pages;
  organization?: Pick<Org, 'areHouseholdsSubscribed' | 'isHouseholdChoice' | 'isUsageBased' | 'notInterested'>;
  household?: Pick<Household, 'status' | 'trialStartDate' | 'isDiscounted'>;
  clientDate: DateString;
  isOrganizationHymnal?: boolean;
  isBypassUser?: boolean;
  isOptimisticallySubscribed?: boolean;
}): SongClickGateDecision {
  const hasOrganization = !!organization && !organization.notInterested;
  if (
    hasSubscriptionLikeSongAccess({
      page,
      organization,
      household,
      clientDate,
      isBypassUser,
      isOptimisticallySubscribed,
    })
  ) {
    return { variant: 'allow' };
  }

  if (!hasOrganization) {
    if (page !== Pages.Library) {
      return { variant: 'allow' };
    }
    return getTrialAwareSongClickGateDecision({ household, clientDate });
  }

  if (!organization.isHouseholdChoice) {
    if (page === Pages.Library && isOrganizationHymnal) {
      return { variant: 'allow' };
    }
    if (page !== Pages.Library || organization.areHouseholdsSubscribed) {
      return { variant: 'allow' };
    }
    return getTrialAwareSongClickGateDecision({ household, clientDate });
  }

  if (hasHouseholdChoiceSundaySongAccess({ page, organization, clientDate })) {
    return { variant: 'allow' };
  }
  if (![Pages.Today, Pages.Organization, Pages.Favorites, Pages.Library].includes(page as Pages)) {
    return { variant: 'allow' };
  }
  return getTrialAwareSongClickGateDecision({ household, clientDate });
}

export function hasSubscriptionLikeSongAccess({
  page,
  organization,
  household,
  clientDate,
  isBypassUser = false,
  isOptimisticallySubscribed = false,
}: {
  page?: Pages;
  organization?: Pick<Org, 'areHouseholdsSubscribed' | 'isHouseholdChoice' | 'isUsageBased' | 'notInterested'>;
  household?: Pick<Household, 'status' | 'trialStartDate' | 'isDiscounted'>;
  clientDate: DateString;
  // internal (Sing Your Part) users - org admin/staff roles no longer bypass (GitHub issue #3399); they may
  // discount the household instead, which arrives here as household.isDiscounted
  isBypassUser?: boolean;
  isOptimisticallySubscribed?: boolean;
}): boolean {
  if (
    household?.status === HouseholdStatus.Subscribed ||
    household?.isDiscounted ||
    isOptimisticallySubscribed ||
    isBypassUser
  ) {
    return true;
  }
  // membership in a Usage-Based Organization grants full access; the Organization is billed per active household
  if (organization && !organization.notInterested && organization.isUsageBased) {
    return true;
  }
  const trialState = deriveTrialState({ trialStartDate: household?.trialStartDate, clientDate });
  if (trialState.status !== 'active') {
    return false;
  }
  if (!organization || organization.notInterested) {
    return page === Pages.Library;
  }
  if (organization.isHouseholdChoice) {
    return [Pages.Today, Pages.Organization, Pages.Favorites, Pages.Library].includes(page as Pages);
  }
  return page === Pages.Library && !organization.areHouseholdsSubscribed;
}

export function hasHouseholdChoiceSundaySongAccess({
  page,
  organization,
  clientDate,
}: {
  page?: Pages;
  organization?: Pick<Org, 'isHouseholdChoice' | 'notInterested'>;
  clientDate: DateString;
}): boolean {
  if (!organization) {
    return false;
  }
  return (
    !organization.notInterested &&
    organization.isHouseholdChoice === true &&
    (page === Pages.Today || page === Pages.Organization) &&
    isSunday(clientDate)
  );
}

function getTrialAwareSubscriptionBannerDecision({
  household,
  clientDate,
}: {
  household?: Pick<Household, 'trialStartDate'>;
  clientDate: DateString;
}): SubscriptionBannerDecision {
  const trialState = deriveTrialState({ trialStartDate: household?.trialStartDate, clientDate });
  switch (trialState.status) {
    case 'pre-trial':
      return { variant: 'subscribe' };
    case 'active':
      return { variant: 'active-trial', daysLeft: trialState.daysLeft };
    case 'expired':
      return { variant: 'expired-trial' };
  }
}

function getTrialAwareSongClickGateDecision({
  household,
  clientDate,
}: {
  household?: Pick<Household, 'trialStartDate'>;
  clientDate: DateString;
}): SongClickGateDecision {
  const trialState = deriveTrialState({ trialStartDate: household?.trialStartDate, clientDate });
  switch (trialState.status) {
    case 'active':
      return { variant: 'allow' };
    case 'pre-trial':
      return { variant: 'gate', trialStatus: 'pre-trial' };
    case 'expired':
      return { variant: 'gate', trialStatus: 'expired' };
  }
}

function isSunday(clientDate: DateString): boolean {
  return getDayOfWeek(clientDate) === DayOfWeek.Sunday;
}
